import { useCallback, useEffect, useRef, useState } from 'react';
import type { Edge, Node, Viewport } from '@xyflow/react';

/**
 * Local persistence for Workflow canvases.
 *
 * localStorage-based today. When we ship a real workflow_drafts table on
 * the worker, swap the internal `readDraft` / `writeDraft` bodies for
 * fetch calls — the hook contract stays the same, so Workflow.tsx doesn't
 * need to change.
 */

export interface WorkflowDraft {
  id: string;
  title: string;
  rootBrief: string;
  nodes: Node[];
  edges: Edge[];
  viewport?: Viewport;
  updatedAt: string; // ISO timestamp
}

const STORAGE_PREFIX = 'planbase_workflow_draft:';
const INDEX_KEY = 'planbase_workflow_drafts_index';

function readDraft(id: string): WorkflowDraft | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + id);
    if (!raw) return null;
    return JSON.parse(raw) as WorkflowDraft;
  } catch {
    return null;
  }
}

function writeDraft(draft: WorkflowDraft): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + draft.id, JSON.stringify(draft));
    // Maintain a lightweight index so we can list drafts later.
    const rawIndex = localStorage.getItem(INDEX_KEY);
    const index: Record<string, { title: string; updatedAt: string }> = rawIndex
      ? JSON.parse(rawIndex)
      : {};
    index[draft.id] = { title: draft.title, updatedAt: draft.updatedAt };
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
    // Wake any listeners so the Orbi sidebar's "Workflows" section
    // refreshes as the user creates and renames drafts.
    notifyDraftListeners();
  } catch {
    // Storage full or blocked — swallow silently. UI stays responsive.
  }
}

function deleteDraftStorage(id: string): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + id);
    const rawIndex = localStorage.getItem(INDEX_KEY);
    if (rawIndex) {
      const index = JSON.parse(rawIndex);
      delete index[id];
      localStorage.setItem(INDEX_KEY, JSON.stringify(index));
    }
    notifyDraftListeners();
  } catch {
    // ignore
  }
}

/**
 * Lightweight pub/sub so the Orbi sidebar's Workflows section stays in
 * sync as drafts are created, renamed, or removed from anywhere in the
 * app — including cross-tab via the `storage` event.
 */
const draftListeners = new Set<() => void>();
function notifyDraftListeners() {
  for (const cb of draftListeners) cb();
}
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === INDEX_KEY || e.key?.startsWith(STORAGE_PREFIX)) {
      notifyDraftListeners();
    }
  });
}

/** Short, url-safe id — nanoid-style but no dependency. */
export function newDraftId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseWorkflowDraftOptions {
  /** draftId from the URL, or undefined for a fresh /orbi/workflow visit. */
  draftId: string | undefined;
  /** How long to wait after the last change before writing. Default 800ms. */
  debounceMs?: number;
}

export function useWorkflowDraft({ draftId, debounceMs = 800 }: UseWorkflowDraftOptions) {
  const [hydrated, setHydrated] = useState<WorkflowDraft | null>(null);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hydrate once per draftId change.
  useEffect(() => {
    if (!draftId) {
      setHydrated(null);
      setStatus('idle');
      return;
    }
    const draft = readDraft(draftId);
    setHydrated(draft);
    setStatus(draft ? 'saved' : 'idle');
    setLastSavedAt(draft ? new Date(draft.updatedAt).getTime() : null);
  }, [draftId]);

  /**
   * Schedule a debounced save. Call this on every canvas change — the
   * hook will collapse rapid updates into one write.
   */
  const save = useCallback(
    (draft: Omit<WorkflowDraft, 'updatedAt'>) => {
      if (!draft.id) return;
      setStatus('saving');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const full: WorkflowDraft = { ...draft, updatedAt: new Date().toISOString() };
        writeDraft(full);
        setStatus('saved');
        setLastSavedAt(Date.now());
      }, debounceMs);
    },
    [debounceMs],
  );

  const removeDraft = useCallback((id: string) => {
    deleteDraftStorage(id);
  }, []);

  // Flush on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { hydrated, status, lastSavedAt, save, removeDraft };
}

export interface WorkflowDraftSummary {
  id: string;
  title: string;
  updatedAt: string;
}

/** List all locally-saved drafts, newest first. Used by the sidebar's Workflows section. */
export function listDrafts(): WorkflowDraftSummary[] {
  try {
    const rawIndex = localStorage.getItem(INDEX_KEY);
    if (!rawIndex) return [];
    const index = JSON.parse(rawIndex) as Record<string, { title: string; updatedAt: string }>;
    return Object.entries(index)
      .map(([id, meta]) => ({ id, ...meta }))
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  } catch {
    return [];
  }
}

/**
 * Reactive draft-list hook. Refreshes whenever any writeDraft or
 * deleteDraftStorage runs (in this tab or another), so the sidebar
 * shows a new workflow the moment it's created.
 */
export function useWorkflowDrafts(): WorkflowDraftSummary[] {
  const [drafts, setDrafts] = useState<WorkflowDraftSummary[]>(() => listDrafts());
  useEffect(() => {
    const cb = () => setDrafts(listDrafts());
    draftListeners.add(cb);
    return () => { draftListeners.delete(cb); };
  }, []);
  return drafts;
}

/** Delete a draft and refresh listeners. */
export function deleteDraft(id: string): void {
  deleteDraftStorage(id);
}
